import { IRouterLink } from './i-router-link';
import { Language } from '../dto/language';
import { TranslationMode } from '../dto/translation-mode';

/**
 * Client-side routing abstraction.
 * Framework-agnostic routing operations for navigation and route parameter access.
 */
export abstract class IClientRouting {
	/**
	 * Get a route parameter value
	 * @param param - Parameter name
	 * @returns Parameter value or null/undefined if not found
	 */
	abstract getRouteParam(param: string): string | null | undefined;

	/**
	 * Get all route parameters
	 * @returns Object with all route parameters
	 */
	abstract getRouteParams(): Record<string, string | undefined>;

	/**
	 * Navigate to a given link
	 * @param link - Router link object
	 */
	abstract navigate(link: IRouterLink): Promise<void>;

	/**
	 * Replace current route (no history entry)
	 * @param link - Router link object
	 */
	abstract replace(link: IRouterLink): Promise<void>;

	/**
	 * Go back in history
	 */
	abstract goBack(): void;

	// Link builders for common routes

	/**
	 * Get home/intro page link
	 */
	abstract getIntroLink(): IRouterLink;

	/**
	 * Get login page link
	 */
	abstract getLoginLink(): IRouterLink;

	/**
	 * Get register page link
	 */
	abstract getRegisterLink(): IRouterLink;

	/**
	 * Get settings page link
	 */
	abstract getSettingsLink(): IRouterLink;

	/**
	 * Get my settings link (user profile settings)
	 */
	abstract getMySettingsLink(): IRouterLink;

	/**
	 * Get user categories management link
	 */
	abstract getUserCategoriesLink(): IRouterLink;

	/**
	 * Get admin panel link
	 */
	abstract getAdminLink(): IRouterLink;

	/**
	 * Get home link with language and category
	 */
	abstract getHomeLink(
		nativeLanguage: Language,
		learnedLanguage: Language,
		mode: TranslationMode,
		categoryId?: string
	): IRouterLink;

	/**
	 * Get category/news list link
	 */
	abstract getCategoryLink(
		nativeLanguage: Language,
		learnedLanguage: Language,
		mode: TranslationMode,
		categoryId: string
	): IRouterLink;

	/**
	 * Get news list link (alias for getCategoryLink)
	 */
	abstract getNewsListLink(
		nativeLanguage: Language,
		learnedLanguage: Language,
		mode: TranslationMode,
		categoryId?: string
	): IRouterLink;

	/**
	 * Get article link
	 */
	abstract getArticleLink(
		nativeLanguage: Language,
		learnedLanguage: Language,
		mode: TranslationMode,
		categoryId: string,
		translationId: string
	): IRouterLink;

	/**
	 * Get word collection link
	 */
	abstract getWordCollectionLink(nativeLanguage: Language, learnedLanguage: Language): IRouterLink;

	/**
	 * Get word repeat/review link
	 */
	abstract getWordRepeatLink(nativeLanguage: Language, learnedLanguage: Language): IRouterLink;

	/**
	 * Get quiz link
	 */
	abstract getQuizLink(nativeLanguage: Language, learnedLanguage: Language): IRouterLink;
}
